import { useState } from 'react';

import { parseTableBlock } from '../../lib/pumsemPaste';
import type { PumsemTable } from '../../model/pumsem.type';
import { BTN_GHOST, BTN_PRIMARY, FIELD } from './pumsemFormStyles';

interface PumsemTablePasteProps {
  onApply: (table: PumsemTable) => void;
  onCancel: () => void;
}

/** 붙여넣은 격자에서 열·행 키를 만든다. 사람이 지을 이름이 아니다. */
const columnKey = (index: number) => `c${index + 1}`;
const rowKey = (index: number) => `r${index + 1}`;

/**
 * 발행본에서 표를 **긁어 붙여** 만든다.
 *
 * 982쪽을 한 칸씩 타이핑하는 건 현실적이지 않다. PDF 뷰어에서 표 블록을 긁어
 * 여기 붙이면 열·행을 만들어 준다. 첫 줄을 머리글로 쓸지는 사람이 정한다 —
 * 원본에는 머리글이 두 줄인 표도, 아예 없는 표도 있어서 자동 판정이 틀린다.
 *
 * 붙여넣기가 완벽할 수 없다는 걸 전제로 만들었다. 미리보기를 먼저 보여주고,
 * 적용한 뒤에도 표 편집기에서 그대로 고칠 수 있다.
 */
export function PumsemTablePaste({ onApply, onCancel }: PumsemTablePasteProps) {
  const [text, setText] = useState('');
  const [headerRow, setHeaderRow] = useState(true);

  const grid = text.trim() ? parseTableBlock(text) : [];
  const header = headerRow ? grid[0] : null;
  const bodyRows = headerRow ? grid.slice(1) : grid;

  // 열 개수는 **가장 넓은 행**에 맞춘다. 원본은 병합·빈칸 때문에 행마다
  // 셀 수가 다른데, 짧은 행에 맞추면 오른쪽 값이 잘려 나간다.
  const width = grid.reduce((max, row) => Math.max(max, row.length), 0);
  // 첫 열은 '구분'(행 이름)이라 값 열에서 뺀다.
  const valueCount = Math.max(0, width - 1);

  const apply = () => {
    const columns = Array.from({ length: valueCount }, (_, index) => ({
      key: columnKey(index),
      label: header?.[index + 1] ?? '',
      group: null,
    }));

    onApply({
      caption: '',
      valueUnit: null,
      rowHeader: header?.[0] || '구분',
      columnHeader: null,
      columns,
      rows: bodyRows.map((cells, rowIndex) => ({
        key: rowKey(rowIndex),
        label: cells[0] ?? '',
        kind: 'LABOR' as const,
        values: Object.fromEntries(
          columns.map((column, index) => [column.key, cells[index + 1] ?? null]),
        ),
        change: 'UNCHANGED' as const,
        changedColumns: [],
      })),
    });
  };

  return (
    <div className="rounded-xl border border-brand-200 bg-brand-50/40 p-4 dark:border-brand-500/30 dark:bg-brand-500/5">
      <p className="mb-2 text-[13px] font-bold text-neutral-800 dark:text-neutral-100">
        발행본에서 표를 긁어 붙여넣기
      </p>
      <p className="mb-3 text-[12.5px] leading-6 text-neutral-600 dark:text-neutral-400">
        PDF 에서 표 영역을 그대로 복사해 붙여넣으세요. 열은 2칸 이상 공백(또는 탭)
        으로 나눕니다. 벌어진 한글(<code>불 도 저</code>)은 자동으로 붙입니다.
        첫 열은 <b>구분</b>(행 이름)으로 들어갑니다.
      </p>

      <textarea
        className={`${FIELD} h-40 font-mono text-[12.5px] leading-6`}
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={'구 분   단 위   수 량\n특 별 인 부   인   2'}
        aria-label="표 붙여넣기"
      />

      <label className="mt-2 flex items-center gap-2 text-[12.5px] text-neutral-700 dark:text-neutral-300">
        <input
          type="checkbox"
          checked={headerRow}
          onChange={(event) => setHeaderRow(event.target.checked)}
        />
        첫 줄을 머리글로 쓴다
      </label>

      {grid.length > 0 && (
        <div className="mt-3 overflow-x-auto rounded-lg border border-neutral-200 bg-white dark:border-white/12 dark:bg-dark-card">
          <table className="w-full border-collapse text-[12.5px]">
            <tbody>
              {grid.map((cells, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={
                    headerRow && rowIndex === 0
                      ? 'bg-neutral-50 font-bold dark:bg-white/5'
                      : ''
                  }
                >
                  {Array.from({ length: width }, (_, cellIndex) => (
                    <td
                      key={cellIndex}
                      className="border border-neutral-100 px-2 py-1 whitespace-nowrap dark:border-white/8"
                    >
                      {cells[cellIndex] ?? ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {grid.length > 0 && (
        <p className="mt-2 text-[12px] text-neutral-500">
          열 {width}개 · 데이터 {bodyRows.length}행으로 읽었습니다. 어긋난 칸이
          있으면 붙여넣기를 고치거나, 적용한 뒤 표에서 직접 수정하세요.
        </p>
      )}

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          className={BTN_PRIMARY}
          disabled={bodyRows.length === 0}
          onClick={apply}
        >
          이 내용으로 표 만들기
        </button>
        <button type="button" className={BTN_GHOST} onClick={onCancel}>
          취소
        </button>
      </div>
    </div>
  );
}
